import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Tag, X, Edit3, CheckCircle, AlertTriangle, Package } from 'lucide-react';
import liveProductsService from '../services/liveProductsService';

const LiveProducts = ({ miraklConnectionId, connectionName }) => {
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  
  const [search, setSearch] = useState('');
  const [filterDiscount, setFilterDiscount] = useState('ALL');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountMode, setDiscountMode] = useState('single');
  const [discountTarget, setDiscountTarget] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    discountType: 'PERCENTAGE',
    discountValue: 10,
    startDate: '',
    endDate: '',
  });
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState({ price: 0, quantity: 0 });

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: search || undefined,
        discountActive: filterDiscount === 'DISCOUNTED' ? 'true' : filterDiscount === 'NONE' ? 'false' : undefined,
      };
      
      const response = await liveProductsService.getAll(miraklConnectionId, params);
      setProducts(response.data.products);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [miraklConnectionId, pagination.page, pagination.limit, search, filterDiscount]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await liveProductsService.getStats(miraklConnectionId);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [miraklConnectionId]);

  useEffect(() => {
    if (miraklConnectionId) {
      fetchProducts();
      fetchStats();
    }
  }, [miraklConnectionId, fetchProducts, fetchStats]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await liveProductsService.syncFromMirakl(miraklConnectionId);
      alert(`Sync complete! Created: ${response.data.created}, Updated: ${response.data.updated}`);
      fetchProducts();
      fetchStats();
    } catch (error) {
      alert('Sync failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(products.map(p => p.id));
      setSelectAll(true);
    } else {
      setSelectedIds([]);
      setSelectAll(false);
    }
  };

  const handleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const openDiscountModal = (mode, product = null) => {
    setDiscountMode(mode);
    setDiscountTarget(product);
    setDiscountForm({
      discountType: 'PERCENTAGE',
      discountValue: 10,
      startDate: '',
      endDate: '',
    });
    setShowDiscountModal(true);
  };

  const handleApplyDiscount = async () => {
    try {
      const data = {
        discountType: discountForm.discountType,
        discountValue: parseFloat(discountForm.discountValue),
        startDate: discountForm.startDate || undefined,
        endDate: discountForm.endDate || undefined,
      };

      if (discountMode === 'single' && discountTarget) {
        await liveProductsService.applyDiscount(discountTarget.id, data);
        alert('Discount applied!');
      } else if (discountMode === 'bulk') {
        data.productIds = selectedIds;
        const result = await liveProductsService.applyBulkDiscount(data);
        alert(`Discount applied to ${result.data.success} products. Failed: ${result.data.failed}`);
      } else if (discountMode === 'global') {
        const result = await liveProductsService.applyGlobalDiscount(miraklConnectionId, data);
        alert(`Global discount applied to ${result.data.success} products. Failed: ${result.data.failed}`);
      }

      setShowDiscountModal(false);
      setSelectedIds([]);
      fetchProducts();
      fetchStats();
    } catch (error) {
      alert('Error applying discount: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRemoveDiscount = async (product) => {
    if (!window.confirm(`Remove discount from "${product.title}"?`)) return;
    
    try {
      await liveProductsService.removeDiscount(product.id);
      fetchProducts();
      fetchStats();
    } catch (error) {
      alert('Error removing discount: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRemoveBulkDiscount = async () => {
    if (!window.confirm(`Remove discount from ${selectedIds.length} products?`)) return;
    
    try {
      const result = await liveProductsService.removeBulkDiscount(selectedIds);
      alert(`Removed discount from ${result.data.success} products.`);
      setSelectedIds([]);
      fetchProducts();
      fetchStats();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRemoveGlobalDiscount = async () => {
    if (!window.confirm('Remove ALL discounts from ALL products?')) return;
    
    try {
      const result = await liveProductsService.removeGlobalDiscount(miraklConnectionId);
      alert(`Removed discount from ${result.data.success} products.`);
      fetchProducts();
      fetchStats();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const openEditModal = (product) => {
    setEditProduct(product);
    setEditForm({
      price: parseFloat(product.originalPrice),
      quantity: product.quantity,
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      if (editForm.price !== parseFloat(editProduct.originalPrice)) {
        await liveProductsService.updatePrice(editProduct.id, editForm.price);
      }
      if (editForm.quantity !== editProduct.quantity) {
        await liveProductsService.updateStock(editProduct.id, editForm.quantity);
      }
      setShowEditModal(false);
      fetchProducts();
      fetchStats();
    } catch (error) {
      alert('Error saving: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatCurrency = (value) => {
    return `R ${parseFloat(value || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  const getFirstImage = (product) => {
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images[0];
    }
    return null;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Live Products</h1>
          <p className="text-secondary" style={{ marginTop: '4px' }}>
            Products currently live on {connectionName} - Manage pricing & discounts
          </p>
          {stats && (
            <p style={{ marginTop: '8px', fontSize: '13px', color: '#6e6e73' }}>
              Total: <strong>{stats.totalProducts}</strong> |
              Active: <strong>{stats.activeProducts}</strong> |
              On Discount: <strong style={{ color: '#9b59b6' }}>{stats.discountedProducts}</strong> |
              Discount Value: <strong style={{ color: '#e74c3c' }}>{formatCurrency(stats.totalDiscountSavings)}</strong>
            </p>
          )}
        </div>
        <div className="page-header-actions">
          {selectedIds.length > 0 && (
            <>
              <span style={styles.selectedBadge}>{selectedIds.length} selected</span>
              <button className="btn btn-secondary" onClick={() => openDiscountModal('bulk')}>
                <Tag size={16} />
                Apply Discount
              </button>
              <button className="btn btn-secondary" onClick={handleRemoveBulkDiscount} style={{ color: '#e74c3c' }}>
                <X size={16} />
                Remove Discount
              </button>
            </>
          )}
          <button className="btn btn-secondary" onClick={() => openDiscountModal('global')}>
            <Tag size={16} />
            Global Discount
          </button>
          <button className="btn btn-primary" onClick={handleSync} disabled={syncing}>
            <RefreshCw size={16} className={syncing ? 'spinning' : ''} />
            {syncing ? 'Syncing...' : 'Sync from Mirakl'}
          </button>
        </div>
      </div>

      <div style={styles.filters}>
        <div className="tabs">
          {['ALL', 'DISCOUNTED', 'NONE'].map((status) => (
            <button
              key={status}
              className={`tab ${filterDiscount === status ? 'active' : ''}`}
              onClick={() => setFilterDiscount(status)}
            >
              {status === 'ALL' ? 'All Products' : status === 'DISCOUNTED' ? 'On Discount' : 'No Discount'}
              {status === 'DISCOUNTED' && stats?.discountedProducts > 0 && (
                <span style={styles.count}>{stats.discountedProducts}</span>
              )}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search products..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      <div className="card">
        {loading ? (
          <div className="loading">
            <div className="spinner" />
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <Package size={48} />
            <h3>No live products found</h3>
            <p>Click "Sync from Mirakl" to import your live products</p>
            <button className="btn btn-primary" onClick={handleSync} disabled={syncing}>
              <RefreshCw size={16} />
              Sync Products
            </button>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                  </th>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>Original Price</th>
                  <th>Current Price</th>
                  <th>Discount</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} style={product.discountActive ? { backgroundColor: '#fef9f3' } : {}}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(product.id)}
                        onChange={() => handleSelect(product.id)}
                      />
                    </td>
                    <td>
                      <div style={styles.productCell}>
                        {getFirstImage(product) ? (
                          <img src={getFirstImage(product)} alt="" style={styles.productImage} />
                        ) : (
                          <div style={styles.productImagePlaceholder}>
                            <Package size={20} color="#ccc" />
                          </div>
                        )}
                        <div style={styles.productInfo}>
                          <span style={styles.productName} title={product.title}>
                            {product.title}
                          </span>
                          {product.ean && (
                            <span style={styles.productEan}>EAN: {product.ean}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td><span style={styles.sku}>{product.shopSku}</span></td>
                    <td>{formatCurrency(product.originalPrice)}</td>
                    <td style={{ fontWeight: '600', color: product.discountActive ? '#27ae60' : '#1d1d1f' }}>
                      {formatCurrency(product.currentPrice)}
                    </td>
                    <td>
                      {product.discountActive ? (
                        <span className="badge badge-error" style={{ backgroundColor: '#9b59b6', color: '#fff' }}>
                          {product.discountType === 'PERCENTAGE' 
                            ? `${Number(product.discountValue).toFixed(0)}% OFF`
                            : `R${Number(product.discountValue).toFixed(0)} OFF`
                          }
                        </span>
                      ) : (
                        <span style={{ color: '#ccc' }}>—</span>
                      )}
                    </td>
                    <td style={{ color: product.quantity < 5 ? '#e74c3c' : '#1d1d1f', fontWeight: product.quantity < 5 ? '600' : '400' }}>
                      {product.quantity}
                    </td>
                    <td>
                      {product.active ? (
                        <span className="badge badge-success">
                          <CheckCircle size={12} />
                          Active
                        </span>
                      ) : (
                        <span className="badge badge-neutral">Inactive</span>
                      )}
                      {product.syncStatus === 'ERROR' && (
                        <AlertTriangle size={14} color="#e74c3c" style={{ marginLeft: '4px' }} title={product.syncError} />
                      )}
                    </td>
                    <td>
                      <div style={styles.actions}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEditModal(product)} title="Edit Price/Stock">
                          <Edit3 size={16} />
                        </button>
                        {product.discountActive ? (
                          <button 
                            className="btn btn-ghost btn-sm" 
                            onClick={() => handleRemoveDiscount(product)} 
                            title="Remove Discount"
                            style={{ color: '#e74c3c' }}
                          >
                            <X size={16} />
                          </button>
                        ) : (
                          <button 
                            className="btn btn-ghost btn-sm" 
                            onClick={() => openDiscountModal('single', product)} 
                            title="Add Discount"
                            style={{ color: '#9b59b6' }}
                          >
                            <Tag size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination.pages > 1 && (
        <div style={styles.pagination}>
          <button 
            className="btn btn-secondary btn-sm"
            disabled={pagination.page <= 1}
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
          >
            Previous
          </button>
          <span style={{ color: '#6e6e73', fontSize: '14px' }}>Page {pagination.page} of {pagination.pages}</span>
          <button 
            className="btn btn-secondary btn-sm"
            disabled={pagination.page >= pagination.pages}
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
          >
            Next
          </button>
        </div>
      )}

      {/* Discount Modal */}
      {showDiscountModal && (
        <div style={styles.modalOverlay} onClick={() => setShowDiscountModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {discountMode === 'single' && `Apply Discount`}
                {discountMode === 'bulk' && `Apply Discount to ${selectedIds.length} Products`}
                {discountMode === 'global' && 'Apply Global Discount'}
              </h2>
              <button style={styles.closeBtn} onClick={() => setShowDiscountModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div style={styles.modalBody}>
              {discountMode === 'single' && discountTarget && (
                <div style={styles.targetProduct}>
                  {getFirstImage(discountTarget) && (
                    <img src={getFirstImage(discountTarget)} alt="" style={styles.modalProductImage} />
                  )}
                  <div>
                    <p style={styles.modalProductName}>{discountTarget.title}</p>
                    <p style={styles.modalProductPrice}>Current: {formatCurrency(discountTarget.originalPrice)}</p>
                  </div>
                </div>
              )}
              
              {discountMode === 'global' && (
                <div style={styles.warningBox}>
                  <AlertTriangle size={20} />
                  <span>This will apply discount to ALL active products!</span>
                </div>
              )}

              <div style={styles.formGroup}>
                <label>Discount Type</label>
                <select 
                  value={discountForm.discountType}
                  onChange={(e) => setDiscountForm(f => ({ ...f, discountType: e.target.value }))}
                  style={styles.input}
                >
                  <option value="PERCENTAGE">Percentage (%)</option>
                  <option value="FIXED_AMOUNT">Fixed Amount (R)</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label>{discountForm.discountType === 'PERCENTAGE' ? 'Discount %' : 'Discount Amount (R)'}</label>
                <input 
                  type="number"
                  min="0"
                  max={discountForm.discountType === 'PERCENTAGE' ? 100 : 999999}
                  value={discountForm.discountValue}
                  onChange={(e) => setDiscountForm(f => ({ ...f, discountValue: e.target.value }))}
                  style={styles.input}
                />
                {discountMode === 'single' && discountTarget && discountForm.discountType === 'PERCENTAGE' && (
                  <p style={styles.previewPrice}>
                    New price: {formatCurrency(Number(discountTarget.originalPrice) * (1 - discountForm.discountValue / 100))}
                  </p>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label>Start Date (optional)</label>
                  <input 
                    type="datetime-local"
                    value={discountForm.startDate}
                    onChange={(e) => setDiscountForm(f => ({ ...f, startDate: e.target.value }))}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>End Date (optional)</label>
                  <input 
                    type="datetime-local"
                    value={discountForm.endDate}
                    onChange={(e) => setDiscountForm(f => ({ ...f, endDate: e.target.value }))}
                    style={styles.input}
                  />
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button className="btn btn-secondary" onClick={() => setShowDiscountModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleApplyDiscount}>Apply Discount</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editProduct && (
        <div style={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Edit Product</h2>
              <button style={styles.closeBtn} onClick={() => setShowEditModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <div style={styles.targetProduct}>
                {getFirstImage(editProduct) && (
                  <img src={getFirstImage(editProduct)} alt="" style={styles.modalProductImage} />
                )}
                <div>
                  <p style={styles.modalProductName}>{editProduct.title}</p>
                  <p style={styles.modalProductSku}>SKU: {editProduct.shopSku}</p>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label>Base Price (R)</label>
                <input 
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.price}
                  onChange={(e) => setEditForm(f => ({ ...f, price: parseFloat(e.target.value) }))}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label>Stock Quantity</label>
                <input 
                  type="number"
                  min="0"
                  value={editForm.quantity}
                  onChange={(e) => setEditForm(f => ({ ...f, quantity: parseInt(e.target.value) }))}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  filters: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    gap: '16px',
  },
  count: {
    marginLeft: '6px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '600',
    background: 'rgba(155, 89, 182, 0.15)',
    color: '#9b59b6',
    borderRadius: '10px',
  },
  searchInput: {
    padding: '10px 14px',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    fontSize: '14px',
    width: '250px',
  },
  selectedBadge: {
    padding: '8px 12px',
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
  },
  productCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  productImage: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    objectFit: 'cover',
    background: '#f5f5f7',
  },
  productImagePlaceholder: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    background: '#f5f5f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  productInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  productName: {
    fontWeight: '500',
    color: '#1d1d1f',
    fontSize: '14px',
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  productEan: {
    fontSize: '11px',
    color: '#86868b',
    marginTop: '2px',
  },
  sku: {
    fontFamily: 'SF Mono, Monaco, Consolas, monospace',
    fontSize: '13px',
    color: '#6e6e73',
  },
  actions: {
    display: 'flex',
    gap: '4px',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginTop: '20px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1d1d1f',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6e6e73',
  },
  modalBody: {
    padding: '24px',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid #e5e7eb',
  },
  targetProduct: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  modalProductImage: {
    width: '56px',
    height: '56px',
    borderRadius: '8px',
    objectFit: 'cover',
  },
  modalProductName: {
    fontWeight: '600',
    color: '#1d1d1f',
    margin: 0,
    fontSize: '14px',
  },
  modalProductPrice: {
    color: '#6e6e73',
    margin: '4px 0 0',
    fontSize: '13px',
  },
  modalProductSku: {
    color: '#6e6e73',
    margin: '4px 0 0',
    fontSize: '12px',
    fontFamily: 'monospace',
  },
  formGroup: {
    marginBottom: '16px',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '6px',
    boxSizing: 'border-box',
  },
  previewPrice: {
    marginTop: '8px',
    fontSize: '13px',
    color: '#27ae60',
    fontWeight: '600',
  },
  warningBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: '#fff3cd',
    color: '#856404',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
};

export default LiveProducts;